/**
 * Return slice - handles return-related state and operations
 * Maps to ReturnController endpoints
 */
import { PayloadAction, createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import { companyChanged } from 'store/actions/companyActions';
import type { ThunkConf } from 'store/thunks';
import type { RootState } from 'store';
import axios, { AxiosError } from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { returnService } from 'services/returnService';
import { decisionCodeService } from 'services/decisionCodeService';
import { skuLocationThunks } from 'store/reducers/skuLocationSlice';
import { getOrderDeliveryInfo } from './orderSlice';
import { setDecisionCodes, setRuleSetName } from './decisionCodeSlice';
import {
    ReturnApiReturnGetReturnRequest,
    ReturnApiReturnSearchReturnsPOSTRequest,
    ReturnItem,
    ReturnItemRowStatus,
    ReturnOverviewDto,
    ReturnOverviewDtoPageInfo,
    ReturnSlip,
    ReturnAdditionalCostItem,
    ReturnStatusStatistic,
    ReturnStatusType,
    ReturnLogItem,
    OrderForReturnDTO,
    ReturnOrderGroup,
    ReturnOrderLine,
    DetermineDecisionCodesResponse,
    ReturnOrderLineType,
} from 'returnCenter-api';
import { toast } from 'react-toastify';
import { t } from 'i18next';
import {
    ExtendedReturnItem,
    ExtendedAddress,
    LoadingCards,
    SearchReturnPageInfo,
    LocalPropertySettings,
    ReturnItemProperty,
    canAddSplit,
    findOrderLine,
    handleApiError,
} from './shared/sliceTypes';

// Re-export for backwards compatibility
export type { ExtendedReturnItem, ReturnItemProperty } from './shared/sliceTypes';

export const SEARCH_MODE = {
    NONE: 'none',
    CUSTOMER_CODE: 'customerCode',
    CUSTOMER_GROUP: 'customerGroupCode',
} as const;

export type SearchMode = typeof SEARCH_MODE[keyof typeof SEARCH_MODE];

export type OrderByField = 'Id' | 'Created' | 'Changed';
export type OrderByDirection = 'ASC' | 'DESC';

export interface FilterParams {
    query?: string;
    filterInput?: string;
    searchMode?: SearchMode;
    from?: Date;
    to?: Date;
    orderBy?: OrderByField;
    orderByDirection?: OrderByDirection;
}

interface UpdateReturnItemPayload {
    orderNumber: string;
    sku: string;
    orderLineNumber: string;
    itemId?: string | number;
    property: ReturnItemProperty;
    value: any;
    sourceReturnId?: number | null;
}

export interface AvailableOrderLinesState {
    orderNumber: string | null;
    orderLines: ReturnOrderLine[] | null;
    loading: boolean;
    error: boolean;
}

interface ReturnState {
    // Return slip state
    returnSlip: ReturnSlip | null;
    returnSlipLoading: boolean;
    returnSlipErrorOnLoading: boolean;
    customerDetails?: ExtendedAddress;
    orderNumbers: string[];

    // Search returns state
    searchReturns: ReturnOverviewDto[];
    searchReturnPageInfo: SearchReturnPageInfo;
    searchReturnLoading: boolean;
    searchReturnErrorOnLoading: boolean;
    loadingCards: LoadingCards;
    returnId: ReturnOverviewDto['id'] | null;

    // Return statistics
    returnStatistics: ReturnStatusStatistic[] | null;
    returnStatisticsLoading: boolean;
    returnStatisticsErrorOnLoading: boolean;

    // Return logs
    returnLogs: ReturnLogItem[];
    returnLogsLoading: boolean;
    returnLogsError: string | null;

    // UI state
    searchItemsString: string;
    localPropertySettings: LocalPropertySettings;
    changedRows: string[];

    // Error state
    errorMessage: string | null;

    // Filter props
    searchFilters: FilterParams;

    // Unsaved changes tracking
    savedReturnSlipSnapshot: string | null;

    // Available order lines for adding to return slip
    availableOrderLines: AvailableOrderLinesState;
}

const initialState: ReturnState = {
    returnSlip: null,
    returnSlipLoading: false,
    returnSlipErrorOnLoading: false,
    customerDetails: undefined,
    orderNumbers: [],

    searchReturns: [],
    searchReturnPageInfo: { currentPage: 1 },
    searchReturnLoading: false,
    searchReturnErrorOnLoading: false,
    loadingCards: {
        completed: false,
        onHold: false,
        inprogress: false,
        created: false,
        itemsSelected: false,
        incomingPackage: false,
        received: false,
        creditError: false,
        processing: false,
        creditedBatchPending: false,
    },
    returnId: null,

    returnStatistics: null,
    returnStatisticsLoading: false,
    returnStatisticsErrorOnLoading: false,

    returnLogs: [],
    returnLogsLoading: false,
    returnLogsError: null,

    searchItemsString: '',
    localPropertySettings: {},
    changedRows: [],

    errorMessage: null,

    searchFilters: {
        query: '',
        filterInput: undefined,
        searchMode: SEARCH_MODE.NONE,
        orderBy: 'Id',
        orderByDirection: 'DESC',
    },

    savedReturnSlipSnapshot: null,

    availableOrderLines: {
        orderNumber: null,
        orderLines: null,
        loading: false,
        error: false,
    },
};

// ============ THUNKS ============

/**
 * Compound thunk: getReturnById
 *
 * Dispatches to other slices to initialize required data:
 * - decisionCodeSlice: setDecisionCodes, setRuleSetName
 * - skuLocationSlice: fetchSkuLocations
 */
export const getReturnById = createAsyncThunk<ReturnSlip, ReturnApiReturnGetReturnRequest, ThunkConf>(
    'return/getById',
    async ({ returnId }, { dispatch, rejectWithValue }) => {
        try {
            const response = await returnService.getReturn({ returnId });

            // Fetch SKU locations for all SKUs in the return slip
            const skus = [
                ...new Set(
                    (response.returnItems ?? [])
                        .flatMap((group) => group.orderLines ?? [])
                        .map((line) => line.sku)
                        .filter((sku): sku is string => !!sku),
                ),
            ];
            if (skus.length > 0) {
                dispatch(skuLocationThunks.fetchSkuLocations(skus));
            }

            // Fetch delivery info for all orders in the return slip
            const orderNumbers = (response.returnItems ?? [])
                .map((group) => group.orderNumber)
                .filter((orderNumber): orderNumber is string => !!orderNumber);
            if (orderNumbers.length > 0) {
                dispatch(getOrderDeliveryInfo(orderNumbers));
            }

            // Set decision codes from return slip (always update to clear stale state)
            dispatch(setDecisionCodes(response.decisionCodes?.decisionCodes ?? null));
            dispatch(setRuleSetName(response.decisionCodes?.ruleSetName ?? null));

            return response;
        } catch (e) {
            const err = e as AxiosError;
            if (axios.isCancel(err)) {
                return rejectWithValue({ message: 'Request cancelled' });
            }
            return handleApiError({ error: err, rejectWithValue });
        }
    },
);

export const createReturnSlip = createAsyncThunk<
    ReturnSlip,
    { slip: ReturnSlip; returnStatus?: ReturnStatusType },
    ThunkConf
>('return/create', async ({ slip, returnStatus }, { rejectWithValue, dispatch }) => {
    try {
        const response = await returnService.createReturn({
            returnSlip: { ...slip, status: returnStatus },
        });

        toast.success(t('slipCreatedSuccessfully'), {
            autoClose: 3500,
            closeOnClick: true,
        });

        dispatch(getReturnStatistics());
        return response;
    } catch (error) {
        if (axios.isCancel(error)) {
            return rejectWithValue({ message: 'Request cancelled' });
        }
        toast.error(t('errorCreatingReturn'), {
            autoClose: 3500,
            closeOnClick: true,
        });
        return rejectWithValue({ message: error instanceof Error ? error.toString() : 'Unknown error' });
    }
});

export const updateReturnSlip = createAsyncThunk<
    ReturnSlip,
    { slip: ReturnSlip; returnId?: number; returnStatus?: ReturnStatusType },
    ThunkConf
>('return/update', async ({ slip }, { rejectWithValue, dispatch }) => {
    try {
        const response = await returnService.updateReturn({ returnSlip: slip });
        dispatch(getReturnStatistics());
        toast.success(t('slipUpdatedSuccessfully'), {
            autoClose: 3500,
            closeOnClick: true,
        });
        return response;
    } catch (error) {
        if (axios.isCancel(error)) {
            return rejectWithValue({ message: 'Request cancelled' });
        }
        toast.error(t('errorUpdatingReturn'), {
            autoClose: 3500,
            closeOnClick: true,
        });
        return rejectWithValue({ message: error instanceof Error ? error.toString() : 'Unknown error' });
    }
});

export const deleteReturnSlip = createAsyncThunk<boolean, number, ThunkConf>(
    'return/delete',
    async (returnId, { rejectWithValue, dispatch }) => {
        try {
            const response = await returnService.deleteReturn({ returnId });

            toast.success(t('returnSlipDeleted'), {
                autoClose: 3500,
                closeOnClick: true,
            });

            dispatch(getReturnStatistics());
            return response;
        } catch (error) {
            if (axios.isCancel(error)) {
                return rejectWithValue({ message: 'Request cancelled' });
            }
            if (axios.isAxiosError(error) && error.response?.status === 409) {
                toast.error(t('cannotDeleteLockedByBatch'), {
                    autoClose: 5000,
                    closeOnClick: true,
                });
                return rejectWithValue({ message: 'Return is locked by a sent batch return' });
            }
            toast.error(t('errorDeletingReturn'), {
                autoClose: 3500,
                closeOnClick: true,
            });
            return rejectWithValue({ message: error instanceof Error ? error.toString() : 'Unknown error' });
        }
    },
);

/**
 * Compound thunk: createReturnCredit
 *
 * Calls the backend which:
 * 1. Persists return data via UpdateReturn
 * 2. Creates credits against ERP (with retry/continuation on failure)
 * 3. Sets status to Completed or CreditError
 * Returns the final ReturnSlip with updated status.
 */
export const createReturnCredit = createAsyncThunk<
    ReturnSlip,
    { slip: ReturnSlip; returnId?: number; returnStatus?: ReturnStatusType },
    ThunkConf
>('return/createCredit', async ({ slip }, { rejectWithValue, dispatch }) => {
    try {
        const response = await returnService.createReturnCredit({ returnSlip: slip });

        dispatch(getReturnStatistics());

        if (response.status === ReturnStatusType.CreditError) {
            toast.warning(t('someCreditsFailedCheckLogs'), {
                autoClose: 5000,
                closeOnClick: true,
            });
        } else {
            toast.success(t('slipCreatedSuccessfully'), {
                autoClose: 3500,
                closeOnClick: true,
            });
        }

        return response;
    } catch (error) {
        if (axios.isCancel(error)) {
            return rejectWithValue({ message: 'Request cancelled' });
        }
        toast.error(t('errorCreatingCredit'), {
            autoClose: 3500,
            closeOnClick: true,
        });
        return rejectWithValue({ message: error instanceof Error ? error.toString() : 'Unknown error' });
    }
});

export const searchReturns = createAsyncThunk<
    ReturnOverviewDtoPageInfo,
    ReturnApiReturnSearchReturnsPOSTRequest,
    ThunkConf
>('return/search', async (payload, { rejectWithValue }) => {
    try {
        return await returnService.searchReturns(payload);
    } catch (error) {
        toast.error(t('errorSearchingReturns'), {
            autoClose: 3500,
            closeOnClick: true,
        });
        return rejectWithValue({ message: error instanceof Error ? error.toString() : 'Unknown error' });
    }
});

export const getReturnStatistics = createAsyncThunk<ReturnStatusStatistic[], void, ThunkConf>(
    'return/statistics',
    async (_, { rejectWithValue }) => {
        try {
            return await returnService.getReturnStatistics();
        } catch (error) {
            if (axios.isCancel(error)) {
                return rejectWithValue({ message: 'Request cancelled' });
            }
            return rejectWithValue({ message: error instanceof Error ? error.toString() : 'Unknown error' });
        }
    },
);

export const getReturnLogs = createAsyncThunk<ReturnLogItem[], number, ThunkConf>(
    'return/logs/get',
    async (returnId, { rejectWithValue }) => {
        try {
            return await returnService.getReturnLogs({ returnId });
        } catch (error) {
            console.error('Error fetching return logs:', error);
            if (axios.isCancel(error)) {
                return rejectWithValue(undefined);
            }
            return rejectWithValue({ message: error instanceof Error ? error.toString() : 'Unknown error' });
        }
    },
);

export const addReturnLog = createAsyncThunk<void, { returnId: number; comment: string }, ThunkConf>(
    'return/logs/add',
    async ({ returnId, comment }, { rejectWithValue, dispatch }) => {
        try {
            await returnService.addReturnLog({
                returnId,
                addReturnLogRequest: { comment, isInternal: true, pinned: true },
            });
            dispatch(getReturnLogs(returnId));
            toast.success(t('logAddedSuccessfully'), {
                autoClose: 3500,
                closeOnClick: true,
            });
        } catch (error) {
            console.error('Error adding return log:', error);
            if (axios.isCancel(error)) {
                return rejectWithValue(undefined);
            }
            toast.error(t('failedToAddLog'), {
                autoClose: 3500,
                closeOnClick: true,
            });
            return rejectWithValue({ message: error instanceof Error ? error.toString() : 'Unknown error' });
        }
    },
);

export const toggleReturnLogPinned = createAsyncThunk<
    void,
    { logId: number; pinned: boolean; returnId: number },
    ThunkConf
>('return/logs/togglePinned', async ({ logId, pinned, returnId }, { rejectWithValue, dispatch }) => {
    try {
        await returnService.updateReturnLogPinned({ logId, body: pinned });
        dispatch(getReturnLogs(returnId));
    } catch (error) {
        console.error('Error toggling return log pinned:', error);
        if (axios.isCancel(error)) {
            return rejectWithValue(undefined);
        }
        return rejectWithValue({ message: error instanceof Error ? error.toString() : 'Unknown error' });
    }
});

export const getAvailableOrderLinesForOrder = createAsyncThunk<
    ReturnOrderGroup[],
    string[], // orderNumber
    ThunkConf
>('return/getAvailableOrderLinesForOrder', async (orderNumbers) => {
    const result = await returnService.getAvailableOrderLines(orderNumbers);
    console.log(`Fetched available order lines for orders: ${orderNumbers.join(', ')}, result:`, result);
    return result;
});

// ============ DECISION CODE EVALUATION THUNKS ============

interface EvaluateDecisionCodeParams {
    orderNumber: string;
    sku: string;
    orderLineNumber: string;
    itemId: number | string;
    reasonCode: number;
    dispatchDate: string | null;
    orderType?: string | null;
    sourceReturnId?: number | null;
}

export const evaluateDecisionCode = createAsyncThunk<
    DetermineDecisionCodesResponse,
    EvaluateDecisionCodeParams,
    ThunkConf
>(
    'return/evaluateDecisionCode',
    async (
        { orderNumber, itemId, reasonCode, dispatchDate, orderType: paramOrderType },
        { getState, rejectWithValue },
    ) => {
        try {
            const state = getState();
            const returnSlip = state.returns.returnSlip;
            if (!returnSlip) {
                return rejectWithValue({ message: 'No return slip found' });
            }

            const customerId = returnSlip.customerCode ?? undefined;
            const customerGroupCode = returnSlip.customerCard?.groupCode ?? undefined;
            const arrivalDate = returnSlip.arrivalDate?.utc ?? null;

            const orderType = paramOrderType ?? undefined;

            return await decisionCodeService.determineDecisionCodes({
                customerId,
                customerGroupCode,
                arrivalDate,
                items: [
                    {
                        itemId: String(itemId),
                        reasonCode,
                        orderNumber,
                        dispatchDate,
                        orderType,
                    },
                ],
            });
        } catch (error) {
            console.error('Error evaluating decision code:', error);
            if (axios.isCancel(error)) return rejectWithValue(undefined);
            return rejectWithValue({ message: error instanceof Error ? error.toString() : 'Unknown error' });
        }
    },
);

interface BulkEvaluateDecisionCodeItem {
    orderNumber: string;
    sku: string;
    orderLineNumber: string;
    itemId: number | string;
    reasonCode: number;
    dispatchDate: string | null;
    orderType?: string | null;
    sourceReturnId?: number | null;
}

interface BulkEvaluateDecisionCodeParams {
    items: BulkEvaluateDecisionCodeItem[];
}

export const bulkEvaluateDecisionCodes = createAsyncThunk<
    DetermineDecisionCodesResponse,
    BulkEvaluateDecisionCodeParams,
    ThunkConf
>('return/bulkEvaluateDecisionCodes', async ({ items }, { getState, rejectWithValue }) => {
    try {
        const state = getState();
        const returnSlip = state.returns.returnSlip;
        if (!returnSlip) {
            return rejectWithValue({ message: 'No return slip found' });
        }

        const customerId = returnSlip.customerCode ?? undefined;
        const customerGroupCode = returnSlip.customerCard?.groupCode ?? undefined;
        const arrivalDate = returnSlip.arrivalDate?.utc ?? null;

        const requestItems = items.map((item) => ({
            itemId: String(item.itemId),
            reasonCode: item.reasonCode,
            orderNumber: item.orderNumber,
            dispatchDate: item.dispatchDate,
            orderType: item.orderType ?? undefined,
        }));

        return await decisionCodeService.determineDecisionCodes({
            customerId,
            customerGroupCode,
            arrivalDate,
            items: requestItems,
        });
    } catch (error) {
        console.error('Error bulk evaluating decision codes:', error);
        if (axios.isCancel(error)) return rejectWithValue(undefined);
        return rejectWithValue({ message: error instanceof Error ? error.toString() : 'Unknown error' });
    }
});

// ============ SLICE ============

const returnSlice = createSlice({
    name: 'returns',
    initialState,
    reducers: {
        resetReturnState: () => initialState,

        clearReturnSlip(state) {
            state.returnSlip = null;
            state.orderNumbers = [];
            state.customerDetails = undefined;
            state.returnId = null;
            state.availableOrderLines = { orderNumber: null, orderLines: null, loading: false, error: false };
            state.savedReturnSlipSnapshot = null;
        },

        clearErrors(state) {
            state.returnSlipErrorOnLoading = false;
            state.errorMessage = null;
        },

        setReturnSlip(state, action: PayloadAction<ReturnSlip | null>) {
            state.returnSlip = action.payload;
        },

        selectedReturnSet(state, action: PayloadAction<ReturnOverviewDto['id'] | null>) {
            state.returnId = action.payload;
        },

        searchItemsStringSet(state, action: PayloadAction<string>) {
            state.searchItemsString = action.payload;
        },

        cardLoadingSet(state, action: PayloadAction<{ isLoading: boolean; type: keyof LoadingCards | undefined }>) {
            const { type, isLoading } = action.payload;
            if (!type) return;
            state.loadingCards[type] = isLoading;
        },

        addOrderNumber(state, action: PayloadAction<string | string[]>) {
            const orderNumber = action.payload;
            if (Array.isArray(orderNumber)) {
                orderNumber.forEach((num) => {
                    if (!state.orderNumbers.includes(num)) {
                        state.orderNumbers.push(num);
                    }
                });
            } else {
                if (!state.orderNumbers.includes(orderNumber)) {
                    state.orderNumbers.push(orderNumber);
                }
            }
        },

        /**
         * Add an order (OrderForReturnDTO) to the return slip.
         * Order lines (including invoice charges) are pre-built by the backend as ReturnOrderLine[].
         */
        addOrderToReturnSlip(
            state,
            action: PayloadAction<{
                order: OrderForReturnDTO;
                fillMaxReturnQty?: boolean;
                marketId?: number;
            }>,
        ) {
            const { order, fillMaxReturnQty, marketId } = action.payload;
            const orderGroup = order.orderGroup;
            const orderNumber = orderGroup?.orderNumber ?? '';
            const customerCard = order.customerCard;

            // Check if order already exists in return slip
            const existingGroup = state.returnSlip?.returnItems?.find((group) => group.orderNumber === orderNumber);
            if (existingGroup) {
                return; // Order already in return slip
            }

            // Enrich order lines with client-side splitIds and apply fillMaxReturnQty
            const enrichedOrderLines: ReturnOrderLine[] = (orderGroup?.orderLines ?? []).map((line) => ({
                ...line,
                returnItems: (line.returnItems ?? []).map(
                    (ri): ExtendedReturnItem => ({
                        ...ri,
                        quantityInReturn:
                            fillMaxReturnQty && line.type === ReturnOrderLineType.Normal
                                ? line.quantityAvailableForReturn ?? line.quantityShipped ?? 0
                                : ri.quantityInReturn ?? 0,
                        splitId: uuidv4(),
                    }),
                ),
            }));

            const newOrderGroup: ReturnOrderGroup = {
                orderNumber: orderNumber,
                orderLines: enrichedOrderLines,
            };

            // Update return slip
            state.returnSlip = {
                ...state.returnSlip,
                returnItems: [newOrderGroup, ...(state.returnSlip?.returnItems ?? [])],
                returnSummaries: {
                    ...(state.returnSlip?.returnSummaries ?? {}),
                    [orderNumber]: order.returnSummaries ?? [],
                },
                decisionCodes: order.decisionCodes ?? state.returnSlip?.decisionCodes,
                customerCode: order.customerCode ?? state.returnSlip?.customerCode,
                customerGroupCode: customerCard?.groupCode ?? state.returnSlip?.customerGroupCode,
                customerCard: customerCard ?? state.returnSlip?.customerCard,
                marketId: marketId ?? state.returnSlip?.marketId,
                arrivalDate: state.returnSlip?.arrivalDate ?? { utc: new Date().toISOString() },
            };

            // Update customer details
            state.customerDetails = {
                ...state.customerDetails,
                customerCode: order.customerCode,
                customerCard: state.customerDetails?.customerCard ?? customerCard,
            };

            // Add order number to list
            if (!state.orderNumbers.includes(orderNumber)) {
                state.orderNumbers.push(orderNumber);
            }
        },

        setAllReturnItemsToMaxQty(state) {
            if (!state.returnSlip?.returnItems) return;
            state.returnSlip.returnItems.forEach((orderGroup) => {
                (orderGroup.orderLines ?? []).forEach((orderLine) => {
                    if (orderLine.type !== ReturnOrderLineType.Normal) return;
                    const maxQty = orderLine.quantityAvailableForReturn ?? orderLine.quantityShipped ?? 0;
                    (orderLine.returnItems ?? []).forEach((ri) => {
                        ri.quantityInReturn = maxQty;
                    });
                });
            });
        },

        setIncludeAllInvoiceCharges(state, action: PayloadAction<boolean>) {
            if (!state.returnSlip?.returnItems) return;
            state.returnSlip.returnItems.forEach((orderGroup) => {
                (orderGroup.orderLines ?? []).forEach((orderLine) => {
                    if (orderLine.type !== ReturnOrderLineType.InvoiceCharge) return;
                    if ((orderLine.quantityAvailableForReturn ?? 0) <= 0) return; // skip already credited
                    (orderLine.returnItems ?? []).forEach((ri) => {
                        ri.quantityInReturn = action.payload ? 1 : 0;
                    });
                });
            });
        },

        incrementReturnItemQtyByScan(state, action: PayloadAction<{ scanKey: string }>) {
            if (!state.returnSlip?.returnItems) return;
            const { scanKey } = action.payload;
            const lowerKey = scanKey.toLowerCase();

            // Collect all order lines matching the scanned EAN or SKU
            const matchingLines: Array<{ orderLine: ReturnOrderLine }> = [];
            for (const orderGroup of state.returnSlip.returnItems) {
                for (const orderLine of orderGroup.orderLines ?? []) {
                    if (
                        orderLine.ean?.trim().toLowerCase() === lowerKey ||
                        orderLine.sku?.trim().toLowerCase() === lowerKey
                    ) {
                        matchingLines.push({ orderLine });
                    }
                }
            }
            if (matchingLines.length === 0) return;

            // Sequential fill: find the first line with remaining capacity
            for (const { orderLine } of matchingLines) {
                const returnItems = orderLine.returnItems as ExtendedReturnItem[];
                if (!returnItems?.length) continue;
                const maxQty = orderLine.quantityAvailableForReturn ?? 0;

                // Find first return item (respects split rows) with remaining capacity
                for (const ri of returnItems) {
                    const otherQty = returnItems
                        .filter((other) => other !== ri)
                        .reduce((sum, other) => sum + (other.quantityInReturn || 0), 0);
                    const riMax = maxQty - otherQty;
                    const riCurrent = ri.quantityInReturn ?? 0;
                    if (riCurrent < riMax) {
                        ri.quantityInReturn = riCurrent + 1;

                        // Auto-approve/un-approve on public returns
                        if (state.returnSlip?.customerIdentifier) {
                            const requested = ri.quantityRequested ?? 0;
                            if (requested > 0) {
                                if (ri.quantityInReturn === requested) {
                                    ri.status = ReturnItemRowStatus.Approved;
                                } else if (ri.quantityInReturn > requested) {
                                    ri.status = ReturnItemRowStatus.Pending;
                                }
                            }
                        }

                        return;
                    }
                }
            }
        },

        populateMissingLpns(state) {
            const returnSlip = state.returnSlip;
            const returnId = returnSlip?.id;

            if (returnId == null || !returnSlip?.returnItems) return;

            const generatedLpn = `RETURN-${returnId}`;

            for (const group of returnSlip.returnItems) {
                for (const line of group.orderLines ?? []) {
                    if (line.type !== ReturnOrderLineType.Normal) continue;

                    for (const item of line.returnItems ?? []) {
                        if (!item.lpn?.trim()) {
                            item.lpn = generatedLpn;
                        }
                    }
                }
            }
        },

        updateReturnItem(state, action: PayloadAction<UpdateReturnItemPayload>) {
            const { orderNumber, sku, orderLineNumber, itemId, property, value, sourceReturnId } = action.payload;
            const orderLine = findOrderLine(state.returnSlip, orderNumber, orderLineNumber, sourceReturnId);
            if (!orderLine || !orderLine.returnItems) {
                console.error(`Order line ${orderLineNumber} with SKU ${sku} not found in order ${orderNumber}`);
                toast.error(t('errorUpdatingReturnItem'), {
                    autoClose: 3500,
                    closeOnClick: true,
                });
                return;
            }
            const itemToUpdate = orderLine.returnItems.find((item: ExtendedReturnItem) => {
                if (item.splitId !== undefined) {
                    return item.splitId === itemId;
                }
                return item.id === itemId;
            });
            if (!itemToUpdate) {
                console.error(`Return item with id ${itemId} not found in order ${orderNumber}`);
                toast.error(t('errorUpdatingReturnItem'), {
                    autoClose: 3500,
                    closeOnClick: true,
                });
                return;
            }
            (itemToUpdate as any)[property] = value;
        },

        addSplitRow(
            state,
            action: PayloadAction<{
                orderNumber: string;
                itemId: number | string;
                sku: string;
                orderLineNumber: string;
                splitId: string;
                splitItem: Partial<ReturnItem>;
                sourceReturnId?: number | null;
            }>,
        ) {
            const { orderNumber, sku, orderLineNumber, itemId, splitId, splitItem, sourceReturnId } = action.payload;
            const orderLine = findOrderLine(state.returnSlip, orderNumber, orderLineNumber, sourceReturnId);
            if (!orderLine) return;

            const canSplitResult = canAddSplit(orderLine, itemId, splitId, splitItem.quantityInReturn);
            if (!canSplitResult) {
                toast.warn(
                    t('cannotReturnMoreThanShipped', {
                        max: orderLine.quantityAvailableForReturn,
                        sku: sku,
                    }),
                    { autoClose: 3500, closeOnClick: true },
                );
                return;
            }

            const lastReturnItem: ReturnItem | null = orderLine.returnItems?.[orderLine.returnItems.length - 1] ?? null;

            const newSplitItem: ExtendedReturnItem = {
                ...splitItem,
                splitId,
                isSplitRow: true,
                reasonCode: lastReturnItem?.reasonCode,
                decisionCode: lastReturnItem?.decisionCode,
                returnToStock: lastReturnItem?.returnToStock,
                percent: lastReturnItem?.percent,
                decisionCodeIsManual: lastReturnItem?.decisionCodeIsManual,
                batchReturnDestinationId: lastReturnItem?.batchReturnDestinationId,
                lpn: lastReturnItem?.lpn ? lastReturnItem?.lpn : undefined,
            };

            if (!orderLine.returnItems) {
                orderLine.returnItems = [];
            }
            orderLine.returnItems.push(newSplitItem);
        },

        removeSplitRow(
            state,
            action: PayloadAction<{
                orderNumber: string;
                sku: string;
                orderLineNumber: string;
                splitId?: string | number;
                sourceReturnId?: number | null;
            }>,
        ) {
            const { orderNumber, orderLineNumber, splitId, sourceReturnId } = action.payload;
            const orderLine = findOrderLine(state.returnSlip, orderNumber, orderLineNumber, sourceReturnId);
            if (!orderLine || !orderLine.returnItems) return;
            orderLine.returnItems = orderLine.returnItems.filter((item: ExtendedReturnItem) => {
                if (splitId !== undefined && item.splitId !== undefined) {
                    return item.splitId !== splitId;
                }
                return item.id !== splitId;
            });
        },

        setReturnAdditionalCostItems(state, action: PayloadAction<ReturnAdditionalCostItem[]>) {
            if (!state.returnSlip) return;
            state.returnSlip.returnAdditionalCostItems = action.payload.map((item) => ({ ...item }));
        },

        addAdditionalCostItem(state, action: PayloadAction<ReturnAdditionalCostItem>) {
            if (!state.returnSlip) return;
            if (!state.returnSlip.returnAdditionalCostItems) {
                state.returnSlip.returnAdditionalCostItems = [];
            }
            state.returnSlip.returnAdditionalCostItems.push({ ...action.payload });
        },

        removeAdditionalCostItem(state, action: PayloadAction<number>) {
            if (!state.returnSlip?.returnAdditionalCostItems) return;
            state.returnSlip.returnAdditionalCostItems = state.returnSlip.returnAdditionalCostItems.filter(
                (_, idx) => idx !== action.payload,
            );
        },

        updateAdditionalCostItem(
            state,
            action: PayloadAction<{ index: number; property: keyof ReturnAdditionalCostItem; value: any }>,
        ) {
            if (!state.returnSlip?.returnAdditionalCostItems) return;
            const { index, property, value } = action.payload;
            const item = state.returnSlip.returnAdditionalCostItems[index];
            if (item) {
                (item as any)[property] = value;
            }
        },

        setCustomerDetails(state, action: PayloadAction<ExtendedAddress | undefined>) {
            state.customerDetails = action.payload;
        },

        setArrivalDate(state, action: PayloadAction<string>) {
            if (state.returnSlip) {
                const utc = action.payload;
                const str = new Date(utc).toLocaleDateString();
                state.returnSlip.arrivalDate = { utc, str };
            }
        },

        removeOrderFromReturnSlip(state, action: PayloadAction<string>) {
            const orderNumber = action.payload;
            if (!state.returnSlip?.returnItems) return;
            state.returnSlip.returnItems = state.returnSlip.returnItems.filter(
                (group) => group.orderNumber !== orderNumber,
            );
            state.orderNumbers = state.orderNumbers.filter((num) => num !== orderNumber);
        },

        setCreditLabel(state, action: PayloadAction<string | null>) {
            if (state.returnSlip) {
                state.returnSlip.creditLabel = action.payload;
            }
        },

        setClaimReference(state, action: PayloadAction<string | null>) {
            if (state.returnSlip) {
                state.returnSlip.claimReference = action.payload;
            }
        },

        setBatchHandled(state, action: PayloadAction<boolean>) {
            if (state.returnSlip) {
                state.returnSlip.batchHandled = action.payload;
            }
        },

        setFilterParams(state, action: PayloadAction<Partial<FilterParams>>) {
            state.searchFilters = { ...state.searchFilters, ...action.payload };
        },

        addAvailableLineToReturn(state, action: PayloadAction<{ orderNumber: string; orderLine: ReturnOrderLine }>) {
            const { orderNumber, orderLine } = action.payload;
            if (!state.returnSlip) return;

            const lineWithItem: ReturnOrderLine = {
                ...orderLine,
                returnItems: [{ id: 0, quantityInReturn: 0, splitId: uuidv4() } as ExtendedReturnItem],
            };

            const existingGroup = state.returnSlip.returnItems?.find((g) => g.orderNumber === orderNumber);
            if (existingGroup) {
                existingGroup.orderLines = [...(existingGroup.orderLines ?? []), lineWithItem];
            } else {
                state.returnSlip.returnItems = [
                    ...(state.returnSlip.returnItems ?? []),
                    { orderNumber, orderLines: [lineWithItem] },
                ];
                if (!state.orderNumbers.includes(orderNumber)) state.orderNumbers.push(orderNumber);
            }

            // Remove from available after adding, also check that it's been removed from the correct order group
            if (state.availableOrderLines.orderLines) {
                const updated = state.availableOrderLines.orderLines.filter(
                    (l) => l.orderLineNumber !== orderLine.orderLineNumber,
                );
                state.availableOrderLines.orderLines = updated.length > 0 ? updated : null;
            }
        },
    },
    extraReducers: (builder) => {
        // getReturnById
        builder
            .addCase(getReturnById.pending, (state) => {
                state.returnSlipLoading = true;
            })
            .addCase(getReturnById.fulfilled, (state, action) => {
                state.returnSlipLoading = false;
                state.returnSlipErrorOnLoading = false;
                state.errorMessage = null;
                state.returnSlip = action.payload;
                state.returnId = action.payload.id ?? null;
                state.availableOrderLines = { orderNumber: null, orderLines: null, loading: false, error: false };
                state.savedReturnSlipSnapshot = JSON.stringify(action.payload);

                const { customerCode, customerCard } = action.payload;
                state.customerDetails = {
                    customerCode: customerCode ?? state.customerDetails?.customerCode,
                    customerCard: customerCard ?? state.customerDetails?.customerCard,
                };
            })
            .addCase(getReturnById.rejected, (state, action) => {
                state.returnSlipLoading = false;
                state.returnSlip = null;
                state.returnSlipErrorOnLoading = true;
                state.errorMessage = (action.payload as any)?.message ?? 'Unknown error';
            });

        // createReturnSlip
        builder
            .addCase(createReturnSlip.pending, (state) => {
                state.returnSlipLoading = true;
            })
            .addCase(createReturnSlip.fulfilled, (state, action) => {
                state.returnSlipLoading = false;
                state.returnSlipErrorOnLoading = false;
                state.returnSlip = action.payload;
                state.returnId = action.payload.id ?? null;
                state.availableOrderLines = { orderNumber: null, orderLines: null, loading: false, error: false };
                state.savedReturnSlipSnapshot = JSON.stringify(action.payload);
            })
            .addCase(createReturnSlip.rejected, (state, action) => {
                state.returnSlipLoading = false;
                state.returnSlipErrorOnLoading = true;
                state.errorMessage = (action.payload as any)?.message ?? 'Unknown error';
            });

        // updateReturnSlip
        builder
            .addCase(updateReturnSlip.pending, (state) => {
                state.returnSlipLoading = true;
            })
            .addCase(updateReturnSlip.fulfilled, (state, action) => {
                state.returnSlip = action.payload;
                state.returnId = action.payload.id ?? state.returnId;
                state.customerDetails = {
                    ...state.customerDetails,
                    customerCode: action.payload.customerCode ?? state.customerDetails?.customerCode,
                    customerCard: action.payload.customerCard ?? state.customerDetails?.customerCard,
                };
                state.returnSlipLoading = false;
                state.returnSlipErrorOnLoading = false;
                state.errorMessage = null;
                state.availableOrderLines = { orderNumber: null, orderLines: null, loading: false, error: false };
                state.savedReturnSlipSnapshot = JSON.stringify(action.payload);
            })
            .addCase(updateReturnSlip.rejected, (state, action) => {
                state.returnSlipLoading = false;
                state.returnSlipErrorOnLoading = true;
                state.errorMessage = (action.payload as any)?.message ?? 'Unknown error';
            });

        // deleteReturnSlip
        builder
            .addCase(deleteReturnSlip.pending, (state) => {
                state.searchReturnLoading = true;
            })
            .addCase(deleteReturnSlip.fulfilled, (state) => {
                state.returnSlip = null;
                state.returnId = null;
                state.savedReturnSlipSnapshot = null;
                state.searchReturnLoading = false;
                state.searchReturnErrorOnLoading = false;
            })
            .addCase(deleteReturnSlip.rejected, (state, action) => {
                state.searchReturnLoading = false;
                state.searchReturnErrorOnLoading = true;
                state.errorMessage = (action.payload as any)?.message ?? 'Unknown error';
            });

        // createReturnCredit
        builder
            .addCase(createReturnCredit.pending, (state) => {
                state.returnSlipLoading = true;
            })
            .addCase(createReturnCredit.fulfilled, (state, action) => {
                state.returnSlip = action.payload;
                state.returnId = action.payload.id ?? state.returnId;
                state.customerDetails = {
                    ...state.customerDetails,
                    customerCode: action.payload.customerCode ?? state.customerDetails?.customerCode,
                    customerCard: action.payload.customerCard ?? state.customerDetails?.customerCard,
                };
                state.returnSlipLoading = false;
                state.returnSlipErrorOnLoading = false;
                state.errorMessage = null;
                state.savedReturnSlipSnapshot = JSON.stringify(action.payload);
            })
            .addCase(createReturnCredit.rejected, (state, action) => {
                state.returnSlipLoading = false;
                state.returnSlipErrorOnLoading = true;
                state.errorMessage = (action.payload as any)?.message ?? 'Unknown error';
            });

        // searchReturns
        builder
            .addCase(searchReturns.pending, (state) => {
                state.searchReturnLoading = true;
                state.searchReturnErrorOnLoading = false;
            })
            .addCase(searchReturns.fulfilled, (state, action) => {
                const {
                    pageSize,
                    currentPage,
                    totalItems,
                    totalPages,
                    items,
                    hasNextPage,
                    hasPreviousPage,
                } = action.payload;
                state.searchReturns = items ?? [];
                state.searchReturnPageInfo = {
                    pageSize: pageSize ?? 10,
                    currentPage: currentPage ?? 1,
                    totalItems: totalItems ?? 0,
                    totalPages: totalPages ?? 0,
                    hasNextPage: hasNextPage ?? false,
                    hasPreviousPage: hasPreviousPage ?? false,
                };
                state.searchReturnLoading = false;
                state.searchReturnErrorOnLoading = false;
                state.errorMessage = null;
                state.loadingCards = {
                    completed: false,
                    onHold: false,
                    inprogress: false,
                    created: false,
                    itemsSelected: false,
                    incomingPackage: false,
                    received: false,
                    creditError: false,
                    processing: false,
                    creditedBatchPending: false,
                };
            })
            .addCase(searchReturns.rejected, (state, action) => {
                state.searchReturnLoading = false;
                state.searchReturnErrorOnLoading = true;
                state.errorMessage = (action.payload as any)?.message ?? 'Unknown error';
            });

        // getReturnStatistics
        builder
            .addCase(getReturnStatistics.pending, (state) => {
                state.returnStatisticsLoading = true;
            })
            .addCase(getReturnStatistics.fulfilled, (state, action) => {
                state.returnStatistics = action.payload;
                state.returnStatisticsLoading = false;
                state.returnStatisticsErrorOnLoading = false;
            })
            .addCase(getReturnStatistics.rejected, (state, action) => {
                state.returnStatisticsLoading = false;
                state.returnStatisticsErrorOnLoading = true;
                state.errorMessage = (action.payload as any)?.message ?? 'Unknown error';
            });

        // getReturnLogs
        builder
            .addCase(getReturnLogs.pending, (state) => {
                state.returnLogsLoading = true;
                state.returnLogsError = null;
            })
            .addCase(getReturnLogs.fulfilled, (state, action) => {
                state.returnLogsLoading = false;
                state.returnLogs = action.payload;
            })
            .addCase(getReturnLogs.rejected, (state, action) => {
                state.returnLogsLoading = false;
                state.returnLogsError = (action.payload as any)?.message ?? 'Unknown error';
            });

        // addReturnLog
        builder
            .addCase(addReturnLog.pending, (state) => {
                state.returnLogsLoading = true;
            })
            .addCase(addReturnLog.fulfilled, (state) => {
                state.returnLogsLoading = false;
            })
            .addCase(addReturnLog.rejected, (state, action) => {
                state.returnLogsLoading = false;
                state.returnLogsError = (action.payload as any)?.message ?? 'Unknown error';
            });

        // ============ DECISION CODE THUNKS (from decisionCodeSlice) ============
        // These update returnItems with the evaluated decision codes

        // evaluateDecisionCode - updates a single item's decision code
        builder.addCase(evaluateDecisionCode.fulfilled, (state, action) => {
            const { orderNumber, orderLineNumber, itemId, sourceReturnId } = action.meta.arg;
            const response = action.payload;

            const orderGroup = state.returnSlip?.returnItems?.find(
                (group) =>
                    group.orderNumber === orderNumber &&
                    (sourceReturnId === undefined || group.sourceReturnId === sourceReturnId),
            );
            const orderLine = orderGroup?.orderLines?.find((line) => line.orderLineNumber === orderLineNumber);
            const itemToUpdate = orderLine?.returnItems?.find((item: ExtendedReturnItem) =>
                item.splitId !== undefined ? item.splitId === itemId : item.id === itemId,
            );

            if (itemToUpdate) {
                if (response.items && response.items.length > 0) {
                    itemToUpdate.decisionCode = response.items[0].decisionCodeId ?? null;
                } else {
                    itemToUpdate.decisionCode = null;
                }
            }
        });

        // bulkEvaluateDecisionCodes - updates multiple items' decision codes
        builder.addCase(bulkEvaluateDecisionCodes.fulfilled, (state, action) => {
            const { items } = action.meta.arg;
            const response = action.payload;

            // Create a map for quick lookup of response items by itemId
            const responseMap = new Map((response.items ?? []).map((ri) => [ri.itemId, ri.decisionCodeId]));

            // Update each item in state
            items.forEach((item) => {
                const orderGroup = state.returnSlip?.returnItems?.find(
                    (group) =>
                        group.orderNumber === item.orderNumber &&
                        (item.sourceReturnId === undefined || group.sourceReturnId === item.sourceReturnId),
                );
                const orderLine = orderGroup?.orderLines?.find((line) => line.orderLineNumber === item.orderLineNumber);
                const itemToUpdate = orderLine?.returnItems?.find((ri: ExtendedReturnItem) =>
                    ri.splitId !== undefined ? ri.splitId === item.itemId : ri.id === item.itemId,
                );

                if (itemToUpdate) {
                    const decisionCodeId = responseMap.get(String(item.itemId));
                    itemToUpdate.decisionCode = decisionCodeId ?? null;
                }
            });
        });

        // Available order lines
        builder.addCase(getAvailableOrderLinesForOrder.pending, (state) => {
            state.availableOrderLines.loading = true;
            state.availableOrderLines.error = false;
        });
        builder.addCase(
            getAvailableOrderLinesForOrder.fulfilled,
            (state, action: PayloadAction<ReturnOrderGroup[]>) => {
                state.availableOrderLines.loading = false;

                if (!state.returnSlip) return;

                const orderNumber = action.payload[0]?.orderNumber ?? '';

                if (!orderNumber) return;

                const fetchedLines = (action.payload[0]?.orderLines ?? []).filter(
                    (l) => (l.quantityAvailableForReturn ?? 0) > 0,
                );

                if (fetchedLines.length === 0) return;

                state.returnSlip.returnItems = state.returnSlip.returnItems ?? [];

                const targetGroup = state.returnSlip.returnItems.find((g) => g.orderNumber === orderNumber);

                const existingLineNumbers = new Set((targetGroup?.orderLines ?? []).map((l) => l.orderLineNumber));

                const linesToAdd = fetchedLines.filter((l) => !existingLineNumbers.has(l.orderLineNumber));

                if (linesToAdd.length === 0) return;

                const enriched = linesToAdd.map((line) => ({
                    ...line,
                    returnItems: [{ id: 0, quantityInReturn: 0, splitId: uuidv4() } as ExtendedReturnItem],
                }));

                if (targetGroup) {
                    targetGroup.orderLines = [...(targetGroup.orderLines ?? []), ...enriched];
                } else {
                    state.returnSlip.returnItems.push({ orderNumber, orderLines: enriched });
                }

                if (!state.orderNumbers.includes(orderNumber)) {
                    state.orderNumbers.push(orderNumber);
                }
            },
        );
        builder.addCase(getAvailableOrderLinesForOrder.rejected, (state, action) => {
            state.availableOrderLines.loading = false;
            state.availableOrderLines.error = (action.payload as any)?.message ?? 'Unknown error';
        });

        // Reset on company change
        builder.addCase(companyChanged, () => initialState);
    },
});

// ============ SELECTORS ============

export const selectReturnSlip = (state: RootState) => state.returns.returnSlip;
export const selectReturnSlipLoading = (state: RootState) => state.returns.returnSlipLoading;
export const selectReturnSlipErrorOnLoading = (state: RootState) => state.returns.returnSlipErrorOnLoading;
export const selectCustomerDetails = (state: RootState) => state.returns.customerDetails;
export const selectOrderNumbers = (state: RootState) => state.returns.orderNumbers;

export const selectSearchReturns = (state: RootState) => state.returns.searchReturns;
export const selectSearchReturnPageInfo = (state: RootState) => state.returns.searchReturnPageInfo;
export const selectSearchReturnsLoading = (state: RootState) => state.returns.searchReturnLoading;
export const selectSearchReturnErrorOnLoading = (state: RootState) => state.returns.searchReturnErrorOnLoading;
export const selectLoadingCards = (state: RootState) => state.returns.loadingCards;
export const selectReturnId = (state: RootState) => state.returns.returnId;

export const selectReturnStatistics = (state: RootState) => state.returns.returnStatistics;
export const selectReturnStatisticsLoading = (state: RootState) => state.returns.returnStatisticsLoading;

export const selectReturnLogs = (state: RootState) => state.returns.returnLogs;
export const selectReturnLogsLoading = (state: RootState) => state.returns.returnLogsLoading;
export const selectReturnLogsError = (state: RootState) => state.returns.returnLogsError;

export const selectSearchItemsString = (state: RootState) => state.returns.searchItemsString;
export const selectErrorMessage = (state: RootState) => state.returns.errorMessage;
export const selectSavedReturnSlipSnapshot = (state: RootState) => state.returns.savedReturnSlipSnapshot;
export const selectSearchFilters = (state: RootState) => state.returns.searchFilters;
export const selectSearchMode = (state: RootState) => state.returns.searchFilters.searchMode;
export const selectAvailableOrderLines = (state: RootState) => state.returns.availableOrderLines;
export const selectAvailableOrderLinesLoading = (state: RootState) => state.returns.availableOrderLines.loading;

// Computed selectors
export const selectReturnSlipStatus = createSelector(selectReturnSlip, (returnSlip) => returnSlip?.status ?? null);

export const selectIsPublicReturn = createSelector(selectReturnSlip, (returnSlip) => !!returnSlip?.customerIdentifier);

export const selectHasUnsavedChanges = createSelector(
    [selectReturnSlip, selectSavedReturnSlipSnapshot],
    (returnSlip, snapshot) => {
        if (!returnSlip || !(returnSlip.returnItems ?? []).length) return false;
        if (snapshot === null) return true;
        return JSON.stringify(returnSlip) !== snapshot;
    },
);

export const selectSelectedOrderNumbers = createSelector(selectReturnSlip, (returnSlip) =>
    (returnSlip?.returnItems ?? []).map((group) => group.orderNumber).filter((num): num is string => !!num),
);

export const selectCreditNotes = createSelector(selectReturnSlip, (returnSlip) => returnSlip?.creditNotes ?? []);

export const selectReturnAdditionalCostItems = createSelector(
    selectReturnSlip,
    (returnSlip) => (returnSlip?.returnAdditionalCostItems ?? []) as ReturnAdditionalCostItem[],
);

export const selectAdditionalCostItemsCount = createSelector(selectReturnAdditionalCostItems, (items) => items.length);

export const selectInvoiceChargeLines = createSelector(selectReturnSlip, (returnSlip) => {
    if (!returnSlip?.returnItems) return [] as (ReturnOrderLine & { orderNumber: string })[];
    return returnSlip.returnItems.flatMap(
        (group) =>
            group.orderLines
                ?.filter((ol) => ol.type === ReturnOrderLineType.InvoiceCharge)
                .map((ol) => ({
                    ...ol,
                    orderNumber: group.orderNumber ?? '',
                })) ?? [],
    );
});

export const selectFilteredReturnItems = createSelector(
    [selectReturnSlip, selectSearchItemsString],
    (returnSlip, searchQuery) => {
        const lowerSearchQuery = searchQuery?.toLowerCase() || '';
        const returnGroups = returnSlip?.returnItems ?? [];

        return returnGroups.map((group) => ({
            orderNumber: group.orderNumber,
            sourceReturnId: group.sourceReturnId,
            orderLines: group.orderLines
                ?.filter((item) => item.type !== ReturnOrderLineType.InvoiceCharge)
                .filter(
                    (item) =>
                        !lowerSearchQuery ||
                        item.productName?.toLowerCase().includes(lowerSearchQuery) ||
                        item.sku?.toLowerCase().includes(lowerSearchQuery) ||
                        item.ean?.toLowerCase().includes(lowerSearchQuery),
                ),
        }));
    },
);

export const selectCustomerCard = createSelector(
    selectCustomerDetails,
    (customerDetails) => customerDetails?.customerCard,
);

export const selectSelectedSalesContact = createSelector(selectCustomerCard, (customerCard) => ({
    salesContact: customerCard?.salesContact ?? null,
    active: customerCard?.active ?? false,
    countryCode: customerCard?.countryCode ?? '',
    code: customerCard?.code ?? '',
    groupCode: customerCard?.groupCode ?? '',
    currency: customerCard?.currency ?? '',
    name: customerCard?.name ?? '',
}));

// Validation selectors
export const selectValidityCheckForReasonCode = createSelector(
    [selectReturnSlip, selectIsPublicReturn],
    (returnSlip, isPublicReturn) => {
        if (!returnSlip || !returnSlip.returnItems) return true;
        const allItems = (returnSlip.returnItems ?? []).flatMap((group) => group.orderLines ?? []);
        // A reason code explains why goods came back, so it only applies to physical lines. An invoice
        // charge counts as shipped so it can be credited, but has no reason of its own to give.
        const shippedItems = allItems.filter(
            (item) => item.type === ReturnOrderLineType.Normal && (item.quantityShipped ?? 0) > 0,
        );
        return shippedItems
            .map(
                (item) =>
                    item.returnItems?.every((subItem) => {
                        const requiresReasonCode =
                            (subItem.quantityInReturn && subItem.quantityInReturn > 0) ||
                            (isPublicReturn && (subItem.quantityRequested ?? 0) > 0);
                        return requiresReasonCode ? subItem.reasonCode !== undefined : true;
                    }) ?? true,
            )
            .every(Boolean);
    },
);

// Import decisionCode selector for validation - needs to be done via state access
export const selectValidityCheckForDecisionCode = createSelector(
    [selectReturnSlip, selectIsPublicReturn, (state: RootState) => state.decisionCode.decisionCodes],
    (returnSlip, isPublicReturn, decisionCodes) => {
        // If decision codes are not enabled, validation always passes
        if (!decisionCodes || decisionCodes.length === 0) return true;
        if (!returnSlip || !returnSlip.returnItems) return true;

        const allItems = (returnSlip.returnItems ?? []).flatMap((group) => group.orderLines ?? []);
        const shippedNormalItems = allItems.filter(
            (item) => item.type === ReturnOrderLineType.Normal && (item.quantityShipped ?? 0) > 0,
        );
        return shippedNormalItems
            .map(
                (item) =>
                    item.returnItems?.every((subItem) => {
                        const requiresDecisionCode =
                            (subItem.quantityInReturn && subItem.quantityInReturn > 0) ||
                            (isPublicReturn && (subItem.quantityRequested ?? 0) > 0);
                        return requiresDecisionCode
                            ? subItem.decisionCode !== undefined && subItem.decisionCode !== null
                            : true;
                    }) ?? true,
            )
            .every(Boolean);
    },
);

export const selectIsReturnItemsQtyValid = createSelector(
    [selectReturnSlip, selectIsPublicReturn],
    (returnSlip, isPublicReturn) => {
        if (!returnSlip) return false;

        const groups = returnSlip.returnItems ?? [];
        const allOrderLines = groups.flatMap((group) => group.orderLines ?? []);

        // Public returns allow all rows to have qty 0 (acts as a check-off for requested items)
        if (isPublicReturn) return true;

        // Must have at least one item with quantityInReturn >= 1
        const anySelected = allOrderLines
            .flatMap((line) => line.returnItems ?? [])
            .some((item) => (item.quantityInReturn ?? 0) >= 1);
        if (!anySelected) return false;

        // For split order lines (more than one return item), ALL items must have quantityInReturn >= 1
        const splitLineInvalid = allOrderLines
            .filter((line) => (line.returnItems ?? []).length > 1)
            .some((line) => (line.returnItems ?? []).some((item) => (item.quantityInReturn ?? 0) < 1));
        if (splitLineInvalid) return false;

        return true;
    },
);

export const selectHasEachOrderAtLeastOneItemWithQty = createSelector(
    [selectReturnSlip, selectIsPublicReturn],
    (returnSlip, isPublicReturn) => {
        if (!returnSlip || !returnSlip.returnItems) return false;

        // Public returns allow all rows to have qty 0 (check-off for requested items)
        if (isPublicReturn) return true;

        const groups = returnSlip.returnItems;
        return groups.every((group) => {
            const orderLines = group.orderLines ?? [];
            return orderLines.some((line) => line.returnItems?.some((item) => (item.quantityInReturn ?? 0) >= 1));
        });
    },
);

// Button state selectors
export const selectCanCompleteReturn = createSelector([selectReturnSlipStatus], (status) => {
    return (
        status === ReturnStatusType.InProgress ||
        status === ReturnStatusType.OnHold ||
        status === ReturnStatusType.CreditError
    );
});

export const selectCanDeleteReturn = createSelector([selectReturnSlipStatus], (status) => {
    return status !== ReturnStatusType.Completed;
});

export const selectCanReceiveGoods = createSelector([selectReturnSlipStatus], (status) => {
    return status !== ReturnStatusType.Completed;
});

export const selectCanPauseReturn = createSelector([selectReturnSlipStatus, selectReturnId], (status, returnId) => {
    return status !== ReturnStatusType.Completed && returnId !== null;
});

export const selectCanSaveReturn = createSelector([selectReturnSlipStatus, selectReturnSlip], (status, returnSlip) => {
    const isEmpty = !returnSlip || Object.keys(returnSlip).length === 0;
    return status !== ReturnStatusType.Completed && !isEmpty;
});

export const selectCanModifyReturn = createSelector([selectReturnSlipStatus], (status) => {
    return status !== ReturnStatusType.Completed && status !== ReturnStatusType.CreditedBatchPending;
});

export const selectHasSplitRows = createSelector([selectReturnSlip], (returnSlip) => {
    if (!returnSlip?.returnItems) return false;
    return returnSlip.returnItems.some((orderGroup) =>
        (orderGroup.orderLines ?? []).some((orderLine) => (orderLine.returnItems ?? []).length > 1),
    );
});

// Combined button states selector
export const selectReturnButtonStates = createSelector(
    [selectReturnSlipStatus, selectReturnId, selectReturnSlip, selectReturnSlipLoading, selectReturnSlipErrorOnLoading],
    (status, returnId, returnSlip, returnSlipLoading, returnSlipError) => {
        const isLoading = returnSlipLoading;
        const hasError = returnSlipError;
        const isCompleted = status === ReturnStatusType.Completed;
        const isCreditError = status === ReturnStatusType.CreditError;
        const isCreditedBatchPending = status === ReturnStatusType.CreditedBatchPending;
        const isOnHold = status === ReturnStatusType.OnHold;
        const isReturnSlip = returnId === null;
        const isEmpty = !returnSlip || Object.keys(returnSlip).length === 0;
        const isTerminal = isCompleted || isCreditError || isCreditedBatchPending;
        const isLockedByBatch = returnSlip?.isLockedByBatch ?? false;
        return {
            canComplete:
                status !== null &&
                status !== ReturnStatusType.Completed &&
                !isCreditedBatchPending &&
                !isLoading &&
                !hasError,
            canDelete: !isLoading && !hasError && !isLockedByBatch,
            canReceiveGoods: !isTerminal && !isLoading && !hasError,
            canPause: !isTerminal && !isReturnSlip && !isLoading && !hasError,
            canSave: !isCompleted && !isCreditError && !isEmpty && !isLoading && !hasError,
            canSetIncoming: !isTerminal && !isReturnSlip && !isLoading && !hasError,
            canModify: !isTerminal && !isLoading && !hasError,
            canCompleteBatch: isCreditedBatchPending && !isLoading && !hasError,
            isLoading,
            hasError,
            isCompleted,
            isCreditError,
            isCreditedBatchPending,
            isOnHold,
            isReturnSlip,
            isLockedByBatch,
        };
    },
);

export const selectBulkReasonCode = createSelector(selectReturnSlip, (returnSlip) => {
    if (!returnSlip) return null;
    let common: number | null = null;
    for (const group of returnSlip.returnItems ?? []) {
        for (const line of group.orderLines ?? []) {
            for (const ri of line.returnItems ?? []) {
                const rc = ri.reasonCode ?? null;
                if (rc == null) continue;
                if (common == null) common = rc;
                else if (common !== rc) return null;
            }
        }
    }
    return common;
});

// An LPN is the pallet the returned goods are received onto, so it only applies to physical lines.
// An invoice charge has nothing to receive — it is credited money, and its row has no LPN input.
export const selectAllM3ItemsHaveLpn = createSelector(selectReturnSlip, (returnSlip) =>
    (returnSlip?.returnItems ?? []).every((group) =>
        (group.orderLines ?? [])
            .filter((line) => line.type === ReturnOrderLineType.Normal)
            .every((line) =>
                (line.returnItems ?? []).every((item) => {
                    const requiresLpn = (item.quantityInReturn ?? 0) > 0;
                    return !requiresLpn || Boolean(item.lpn?.trim());
                }),
            ),
    ),
);

// ============ EXPORTS ============

export const returnActions = returnSlice.actions;
export const {
    resetReturnState,
    clearReturnSlip,
    clearErrors,
    setReturnSlip,
    selectedReturnSet,
    searchItemsStringSet,
    cardLoadingSet,
    addOrderNumber,
    addOrderToReturnSlip,
    updateReturnItem,
    setAllReturnItemsToMaxQty,
    incrementReturnItemQtyByScan,
    addSplitRow,
    removeSplitRow,
    setReturnAdditionalCostItems,
    addAdditionalCostItem,
    removeAdditionalCostItem,
    updateAdditionalCostItem,
    setCustomerDetails,
    setArrivalDate,
    removeOrderFromReturnSlip,
    setCreditLabel,
    setClaimReference,
    setBatchHandled,
    setFilterParams,
    setIncludeAllInvoiceCharges,
    addAvailableLineToReturn,
    populateMissingLpns,
} = returnSlice.actions;

export const returnThunks = {
    getReturnById,
    createReturnSlip,
    updateReturnSlip,
    deleteReturnSlip,
    createReturnCredit,
    searchReturns,
    getReturnStatistics,
    getReturnLogs,
    addReturnLog,
    toggleReturnLogPinned,
    evaluateDecisionCode,
    bulkEvaluateDecisionCodes,
    getAvailableOrderLinesForOrder,
};

export const returnSelectors = {
    selectReturnSlip,
    selectReturnSlipLoading,
    selectReturnSlipErrorOnLoading,
    selectSearchReturnErrorOnLoading,
    selectCustomerDetails,
    selectOrderNumbers,
    selectSearchReturns,
    selectSearchReturnPageInfo,
    selectSearchReturnsLoading,
    selectLoadingCards,
    selectReturnId,
    selectReturnStatistics,
    selectReturnStatisticsLoading,
    selectReturnLogs,
    selectReturnLogsLoading,
    selectReturnLogsError,
    selectSearchItemsString,
    selectErrorMessage,
    selectReturnSlipStatus,
    selectIsPublicReturn,
    selectSelectedOrderNumbers,
    selectCreditNotes,
    selectReturnAdditionalCostItems,
    selectAdditionalCostItemsCount,
    selectInvoiceChargeLines,
    selectIsReturnItemsQtyValid,
    selectFilteredReturnItems,
    selectCustomerCard,
    selectSelectedSalesContact,
    selectValidityCheckForReasonCode,
    selectValidityCheckForDecisionCode,
    selectCanCompleteReturn,
    selectCanDeleteReturn,
    selectCanReceiveGoods,
    selectCanPauseReturn,
    selectCanSaveReturn,
    selectCanModifyReturn,
    selectReturnButtonStates,
    selectHasSplitRows,
    selectHasEachOrderAtLeastOneItemWithQty,
    selectHasUnsavedChanges,
};

export default returnSlice.reducer;
